import { useEffect, useState } from "react";

import { useWorkspaceConnections } from "@web-app/api";
import {
  CurrencyInput,
  Input,
  Label,
  SelectInput,
  TextArea,
} from "@web-app/components";
import {
  FieldValue,
  Order,
  OrderPriorityType,
  TransportType,
} from "@web-app/types";

interface OrderDetailsTabProps {
  debouncedHandleFieldChange: (property: string, value: FieldValue) => void;
  onFieldChange: (property: string, value: FieldValue) => Promise<void>;
  order: Order;
}

const priorityOptions = [
  { label: "Low", value: OrderPriorityType.LOW },
  { label: "High", value: OrderPriorityType.HIGH },
];

export default function OrderDetailsTab({
  order,
  onFieldChange,
  debouncedHandleFieldChange,
}: OrderDetailsTabProps) {
  const { data: workspaceConnections } = useWorkspaceConnections();

  // Local state for optimistic UI updates on currency inputs
  const [localValue, setLocalValue] = useState(
    String(order.details.value || ""),
  );
  const [localTip, setLocalTip] = useState(String(order.details.tip || ""));

  // Sync local state when order changes from external updates
  useEffect(() => {
    setLocalValue(String(order.details.value || ""));
  }, [order.details.value]);

  useEffect(() => {
    setLocalTip(String(order.details.tip || ""));
  }, [order.details.tip]);

  const workspaceOptions = [
    { label: "None", value: "" },
    ...(workspaceConnections?.map((connection) => ({
      label: connection.connectedWorkspace.name,
      value: connection.connectedWorkspace.id,
    })) ?? []),
  ];

  return (
    <div className="space-y-3 lg:space-y-4 px-4 pb-10 lg:pb-20">
      <div className="flex flex-col gap-1">
        <Label>Connected Workspace</Label>
        <SelectInput
          options={workspaceOptions}
          selected={order.connectedWorkspaceId || ""}
          onChange={(value: string) =>
            onFieldChange("connectedWorkspaceId", value)
          }
          placeholder="Select a workspace"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label>Description</Label>
        <TextArea
          value={order.details.description || ""}
          rows={3}
          onConfirm={(value) =>
            onFieldChange("details.description", value ?? "")
          }
          placeholder="Enter order description"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>External Reference ID</Label>
        <Input
          defaultValue={order.externalId || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("externalId", value)
          }
          placeholder="Enter reference ID"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Value</Label>
        <CurrencyInput
          value={localValue}
          onChange={(value: string) => {
            setLocalValue(value);
            debouncedHandleFieldChange("details.value", parseFloat(value) || 0);
          }}
          selectedCurrency={order.details.currency || "USD"}
          onCurrencyChange={(currency: string) =>
            onFieldChange("details.currency", currency)
          }
          placeholder="Enter order value"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Tip</Label>
        <CurrencyInput
          value={localTip}
          onChange={(value: string) => {
            setLocalTip(value);
            debouncedHandleFieldChange("details.tip", parseFloat(value) || 0);
          }}
          selectedCurrency={order.details.currency || "USD"}
          onCurrencyChange={(currency: string) =>
            onFieldChange("details.currency", currency)
          }
          placeholder="Enter tip value"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Service Time (minutes)</Label>
        <Input
          type="number"
          min={0}
          step={1}
          defaultValue={order.details.serviceDuration ?? 0}
          onChange={(value: string) => {
            const duration = Number(value);
            debouncedHandleFieldChange(
              "details.serviceDuration",
              Math.max(0, Number.isFinite(duration) ? Math.round(duration) : 0),
            );
          }}
          placeholder="Enter service time"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Length (cm)</Label>
        <Input
          type="number"
          defaultValue={order.details.length || "0.00"}
          onChange={(value: string) =>
            debouncedHandleFieldChange("details.length", parseFloat(value) || 0)
          }
          placeholder="Enter length of the item in cm"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Width (cm)</Label>
        <Input
          type="number"
          defaultValue={order.details.width || "0.00"}
          onChange={(value: string) =>
            debouncedHandleFieldChange("details.width", parseFloat(value) || 0)
          }
          placeholder="Enter width of the item in cm"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Height (cm)</Label>
        <Input
          type="number"
          defaultValue={order.details.height || "0.00"}
          onChange={(value: string) =>
            debouncedHandleFieldChange("details.height", parseFloat(value) || 0)
          }
          placeholder="Enter height of the item in cm"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Weight (gm)</Label>
        <Input
          type="number"
          defaultValue={order.details.weight || "0.00"}
          onChange={(value: string) =>
            debouncedHandleFieldChange("details.weight", parseFloat(value) || 0)
          }
          placeholder="Enter weight of the item in gm"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Items Count</Label>
        <Input
          type="number"
          defaultValue={order.details.itemsCount || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange(
              "details.itemsCount",
              parseInt(value) || 0,
            )
          }
          placeholder="Enter number of items"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Vehicle Type</Label>
        <SelectInput
          options={Object.entries(TransportType).map(([key, value]) => ({
            label: key,
            value: value,
          }))}
          selected={order.minVehicle || ""}
          onChange={(value: string) => onFieldChange("minVehicle", value)}
          placeholder="Enter vehicle type"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Priority</Label>
        <SelectInput
          options={priorityOptions}
          selected={order.priority || OrderPriorityType.LOW}
          onChange={(value: string) => onFieldChange("priority", value)}
          placeholder="Select priority"
        />
      </div>
    </div>
  );
}
